import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  DatePicker,
  EmptyState,
  Input,
  ReportTable,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import {
  exportCensusReportReportsCensusExportGet,
  getCensusReportClinicOptionsReportsCensusClinicOptionsGet,
  getCensusReportReportsCensusGet,
  printCensusReportReportsCensusPrintGet,
  type Options,
} from '../services/api/sdk.gen';
import { CENSUSSORTBY_VALUES } from '../services/api/models/CensusSortBy';
import { SORTORDER_VALUES } from '../services/api/models/SortOrder';
import type {
  CensusClinicOption,
  CensusReportResult,
  CensusReportRow,
  CensusReportView,
  CensusSortBy,
  ExportCensusReportReportsCensusExportGetData,
  PrintCensusReportReportsCensusPrintGetData,
} from '../services/api/types.gen';
import { formatDateToISO, parseISOToLocalDate } from '../utils/dateUtils';
import {
  coerceReportPageSize,
  formatReportDate,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import { createReportTextColumn } from '../utils/reportColumns';
import { ReportLink } from '../components/reporting/ReportLink';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { buildReportPopulationQuery } from '../components/reporting/reportPopulation';
import { PermissionSlug } from '../constants/featureFlags';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { patientChartPath } from '../utils/navigation';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';

type SortableCensusSortBy = Exclude<CensusSortBy, 'race'>;
const SORTABLE_CENSUS_SORTS: SortableCensusSortBy[] =
  CENSUSSORTBY_VALUES.filter(
    (value): value is SortableCensusSortBy => value !== 'race'
  );
const ALL_CLINICS_VALUE = 'all';

const textColumn = createReportTextColumn<CensusReportRow>;

const dateColumn = (
  id: string,
  header: string,
  accessor: keyof CensusReportRow,
  minWidth = 74,
  sortLabel = header
): ReportTableColumn<CensusReportRow> => ({
  id,
  header,
  accessor: row => formatReportDate(row[accessor] as string | null | undefined),
  enableSorting: true,
  minWidth,
  sortLabel,
});

const baseColumns: ReportTableColumn<CensusReportRow>[] = [
  textColumn('clinic', 'Clinic', 'clinic', 140),
  {
    id: 'patient_first',
    header: 'First',
    accessor: row => row.patient_first,
    render: (_value, row) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {row.patient_first}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 100,
    sortLabel: 'Patient First',
  },
  {
    id: 'patient_last',
    header: 'Last',
    accessor: row => row.patient_last,
    render: (_value, row) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {row.patient_last}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 110,
    sortLabel: 'Patient Last',
  },
  dateColumn('admission_date', 'Admit', 'admission_date', 96, 'Admission Date'),
  dateColumn('discharge_date', 'Disch', 'discharge_date', 96, 'Discharge Date'),
  dateColumn(
    'episode_start_date',
    'Ep Start',
    'episode_start_date',
    96,
    'Episode Start Date'
  ),
  dateColumn(
    'episode_end_date',
    'Ep End',
    'episode_end_date',
    96,
    'Episode End Date'
  ),
  textColumn('mrn', 'MRN', 'mrn', 90),
  textColumn('age', 'Age', 'age', 52),
  textColumn('sex', 'Sex', 'sex', 64),
  textColumn('language', 'Lang', 'language', 100, 'Language'),
  {
    ...textColumn('race', 'Race', 'race', 90),
    enableSorting: false,
  },
  textColumn('address', 'Address', 'address', 180),
  textColumn('city', 'City', 'city', 100),
  textColumn('state', 'St', 'state', 50, 'State'),
  textColumn('county', 'County', 'county', 110),
  textColumn('zip_code', 'Zip', 'zip_code', 72, 'Zip Code'),
  textColumn('phone', 'Phone #', 'phone', 112, 'Phone Number'),
];

const duplicatedColumns: ReportTableColumn<CensusReportRow>[] = [
  ...baseColumns.slice(0, 7),
  textColumn(
    'episode_active',
    'Active',
    'episode_active',
    84,
    'Episode Active'
  ),
  ...baseColumns.slice(7),
];

const columnsByView = {
  census: baseColumns,
  duplicated: duplicatedColumns,
} satisfies Record<CensusReportView, ReportTableColumn<CensusReportRow>[]>;

function readView(value: string | null): CensusReportView {
  return (value === 'duplicated' ? 'duplicated' : 'census') as CensusReportView;
}

const CensusReport = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  const reportsBackHref = useReportsBackHref();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clinics, setClinics] = useState<CensusClinicOption[]>([]);
  const [report, setReport] = useState<CensusReportResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(searchParams.get('q') ?? '');

  const view = readView(searchParams.get('view'));
  const asOf = searchParams.get('as_of') ?? '';
  const dateFrom = searchParams.get('from') ?? '';
  const dateTo = searchParams.get('to') ?? '';
  const hasIncompleteDateRange =
    view === 'duplicated' && Boolean(dateFrom) !== Boolean(dateTo);
  const clinicId = searchParams.get('clinic') ?? '';
  const q = searchParams.get('q') ?? '';
  const includeTestRows = searchParams.get('include_test_rows') === 'true';
  const includeInactiveEpisodes =
    searchParams.get('include_inactive_episodes') === 'true';
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = coerceReportPageSize(Number(searchParams.get('size')));
  const sortBy = readOptionalChoiceParam(
    searchParams.get('sort'),
    SORTABLE_CENSUS_SORTS
  );
  const sortOrder = sortBy
    ? readOptionalChoiceParam(searchParams.get('dir'), SORTORDER_VALUES)
    : undefined;

  const updateParams = useCallback(
    (updates: Record<string, string | null>, resetPage = true) => {
      setSearchParams(current => {
        const next = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        if (resetPage) next.set('page', '1');
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    setSearchText(q);
  }, [q]);

  useEffect(() => {
    if (searchText === q) return;
    const timeoutId = window.setTimeout(() => {
      updateParams({ q: searchText || null });
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [q, searchText, updateParams]);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    getCensusReportClinicOptionsReportsCensusClinicOptionsGet({
      query: {
        view,
        as_of: asOf || null,
        date_from: view === 'duplicated' ? dateFrom || null : null,
        date_to: view === 'duplicated' ? dateTo || null : null,
        ...buildReportPopulationQuery({
          includeTestRows,
          includeInactiveEpisodes,
        }),
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError('Unable to load census report. Please try again.');
          return;
        }
        setClinics(response.data.items);
      })
      .catch(() => {
        if (!cancelled)
          setError('Unable to load census report. Please try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [
    hasPermission,
    view,
    asOf,
    dateFrom,
    dateTo,
    includeInactiveEpisodes,
    includeTestRows,
  ]);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    if (hasIncompleteDateRange) {
      setIsLoading(false);
      setReport(null);
      setError('Select both From and To dates, or clear both.');
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getCensusReportReportsCensusGet({
      query: {
        view,
        as_of: asOf || null,
        date_from: view === 'duplicated' ? dateFrom || null : null,
        date_to: view === 'duplicated' ? dateTo || null : null,
        clinic_id: clinicId || null,
        q: q || null,
        ...buildReportPopulationQuery({
          includeTestRows,
          includeInactiveEpisodes,
        }),
        sort_by: sortBy ?? null,
        sort_order: sortOrder ?? null,
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError(
            getBusinessErrorMessage({
              response: {
                status: response.response?.status,
                data: response.error,
              },
            }) ?? 'Unable to load census report. Please try again.'
          );
          setReport(null);
          return;
        }
        setReport(response.data);
      })
      .catch(error => {
        console.error('Failed to load census report', error);
        if (!cancelled) {
          setError(
            getBusinessErrorMessage(error) ??
              'Unable to load census report. Please try again.'
          );
          setReport(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    hasPermission,
    asOf,
    view,
    dateFrom,
    dateTo,
    hasIncompleteDateRange,
    clinicId,
    q,
    includeTestRows,
    includeInactiveEpisodes,
    sortBy,
    sortOrder,
    page,
    pageSize,
  ]);

  const pagination: ReportTablePaginationState = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );

  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortOrder ?? 'asc' }
    : null;

  const tableColumns = useMemo(() => columnsByView[view], [view]);

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS))
    return <ReportsPermissionAlert />;

  const emptyState = error ? (
    <EmptyState role='alert' title={error} description='Please try again.' />
  ) : (
    <EmptyState
      title='No matching census rows'
      description='Adjust filters and try again.'
    />
  );

  const download = async (format: 'xlsx' | 'pdf') => {
    if (hasIncompleteDateRange) {
      setError('Select both From and To dates, or clear both.');
      return;
    }
    const query = {
      view,
      as_of: asOf || null,
      date_from: view === 'duplicated' ? dateFrom || null : null,
      date_to: view === 'duplicated' ? dateTo || null : null,
      clinic_id: clinicId || null,
      q: q || null,
      ...buildReportPopulationQuery({
        includeTestRows,
        includeInactiveEpisodes,
      }),
      sort_by: sortBy ?? null,
      sort_order: sortOrder ?? null,
    };
    try {
      const response =
        format === 'xlsx'
          ? await exportCensusReportReportsCensusExportGet({
              query,
              responseType: 'blob',
            } satisfies Options<ExportCensusReportReportsCensusExportGetData>)
          : await printCensusReportReportsCensusPrintGet({
              query,
              responseType: 'blob',
            } satisfies Options<PrintCensusReportReportsCensusPrintGetData>);
      if (response.error || !response.data) {
        setError(
          await getUserFacingApiMessageAsync(
            {
              response: {
                status: response.response?.status,
                data: response.response?.data ?? response.error,
              },
            },
            'Unable to download census report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(
        response,
        format === 'xlsx' ? 'census-report.xlsx' : 'census-report.pdf'
      );
    } catch (error) {
      console.error('Failed to download census report', error);
      setError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to download census report. Please try again.'
        )
      );
    }
  };

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-bold'>Census Report</h1>
          <p className='text-sm text-muted-foreground'>
            View active patients or episode-duration rows.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void download('xlsx')}
            disabled={isLoading || hasIncompleteDateRange}
          >
            <Download className='h-4 w-4' /> Export
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void download('pdf')}
            disabled={isLoading || hasIncompleteDateRange}
          >
            <Printer className='h-4 w-4' /> Print
          </Button>
          <ReportPopulationControls
            includeTestRows={includeTestRows}
            includeInactiveEpisodes={includeInactiveEpisodes}
            onIncludeTestRowsChange={checked =>
              updateParams({ include_test_rows: checked ? 'true' : null })
            }
            onIncludeInactiveEpisodesChange={checked =>
              updateParams({
                include_inactive_episodes: checked ? 'true' : null,
              })
            }
          />
        </div>
      </div>

      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className='space-y-4 rounded-lg border bg-card p-4'>
        <div className='flex flex-wrap gap-2'>
          <Button
            variant={view === 'census' ? 'default' : 'outline'}
            size='sm'
            onClick={() =>
              updateParams({
                view: 'census',
                from: null,
                to: null,
                sort: null,
                dir: null,
              })
            }
          >
            Current Census
          </Button>
          <Button
            variant={view === 'duplicated' ? 'default' : 'outline'}
            size='sm'
            onClick={() =>
              updateParams({
                view: 'duplicated',
                sort: 'episode_start_date',
                dir: 'asc',
              })
            }
          >
            Duplicated
          </Button>
        </div>
        <div className='grid gap-3 md:grid-cols-3'>
          <label className='space-y-1 text-sm font-medium'>
            Clinic
            <Select
              value={clinicId || ALL_CLINICS_VALUE}
              onValueChange={value =>
                updateParams({
                  clinic: value === ALL_CLINICS_VALUE ? null : value,
                })
              }
            >
              <SelectTrigger selectSize='default'>
                <SelectValue placeholder='All clinics' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLINICS_VALUE}>All clinics</SelectItem>
                {clinics.map(clinic => (
                  <SelectItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className='space-y-1 text-sm font-medium'>
            Search
            <Input
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              placeholder='Name or MRN'
            />
          </label>
        </div>
        {view === 'duplicated' ? (
          <div className='space-y-2'>
            <div className='grid gap-3 md:grid-cols-4'>
              <label className='space-y-1 text-sm font-medium'>
                From
                <DatePicker
                  date={parseISOToLocalDate(dateFrom)}
                  datePickerSize='default'
                  onDateChange={date =>
                    updateParams({ from: date ? formatDateToISO(date) : null })
                  }
                  showClearButton
                />
              </label>
              <label className='space-y-1 text-sm font-medium'>
                To
                <DatePicker
                  date={parseISOToLocalDate(dateTo)}
                  datePickerSize='default'
                  onDateChange={date =>
                    updateParams({ to: date ? formatDateToISO(date) : null })
                  }
                  showClearButton
                />
              </label>
            </div>
            <p className='text-sm text-muted-foreground'>
              Leave dates blank to show all episodes for active patients.
            </p>
          </div>
        ) : null}
      </div>

      <ReportTable
        data={report?.items ?? []}
        columns={tableColumns}
        getRowId={row => row.row_id}
        caption='Census report results'
        sorting={sorting}
        onSortingChange={next => {
          updateParams(
            next
              ? { sort: next.id, dir: next.direction }
              : { sort: null, dir: null }
          );
        }}
        isLoading={isLoading}
        emptyState={emptyState}
        className='min-w-[1840px] text-xs'
        pagination={{
          ...pagination,
          total: report?.total ?? 0,
          totalPages: Math.ceil((report?.total ?? 0) / pageSize),
          pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
        }}
        onPaginationChange={next => {
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set('page', String(next.page));
            params.set('size', String(next.pageSize));
            return params;
          });
        }}
      />
    </div>
  );
};

export default CensusReport;
